import { useMemo, useState } from 'react';
import { Card, CardContent, Chip, Grid, InputAdornment, TextField, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Content, Header, Link, Page } from '@backstage/core-components';
import raw from './videos.yaml';
import { embedOf, filterVideos, validVideos, Video } from './videoData';

const Player = ({ video }: { video: Video }) => {
  const embed = embedOf(video.url);
  const frame = { position: 'absolute' as const, inset: 0, width: '100%', height: '100%', border: 0 };
  if (embed.kind === 'link') {
    return (
      <Typography variant="body2" style={{ padding: 16 }}>
        Este enlace no se puede insertar: <Link to={embed.src}>ábrelo en su sitio</Link>.
      </Typography>
    );
  }
  return (
    <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
      {embed.kind === 'video' ? (
        <video src={embed.src} controls preload="metadata" style={frame}>
          <track kind="captions" />
        </video>
      ) : (
        <iframe
          src={embed.src}
          title={video.titulo}
          loading="lazy"
          allow="encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={frame}
        />
      )}
    </div>
  );
};

/** Videos de la lista videos.yaml, con buscador y filtro por tag. */
export const VideosPage = () => {
  const videos = useMemo(() => validVideos(raw), []);
  const tags = useMemo(() => [...new Set(videos.flatMap(v => v.tags))].sort(), [videos]);
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState<string>();
  const shown = filterVideos(videos, query, tag);

  return (
    <Page themeId="tool">
      <Header title="Videos" subtitle="Grabaciones y tutoriales de la plataforma" />
      <Content>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar por título, descripción o tag"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Chip label="Todos" color={tag ? 'default' : 'primary'} onClick={() => setTag(undefined)} />
          {tags.map(t => (
            <Chip key={t} label={t} color={tag === t ? 'primary' : 'default'} onClick={() => setTag(tag === t ? undefined : t)} />
          ))}
        </div>
        {!shown.length && (
          <Typography color="textSecondary">{videos.length ? 'Ningún video coincide con la búsqueda.' : 'Todavía no hay videos publicados.'}</Typography>
        )}
        <Grid container spacing={3}>
          {shown.map(v => (
            <Grid item xs={12} md={6} lg={4} key={`${v.url}${v.fecha}`}>
              <Card style={{ height: '100%' }}>
                <Player video={v} />
                <CardContent>
                  <Typography variant="h6">{v.titulo}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(`${v.fecha}T12:00:00`).toLocaleDateString('es-EC', { dateStyle: 'long' })} · <Link to={v.url}>Abrir en su sitio</Link>
                  </Typography>
                  {v.descripcion && <Typography variant="body2" style={{ marginTop: 8 }}>{v.descripcion}</Typography>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {v.tags.map(t => (
                      <Chip key={t} size="small" label={t} onClick={() => setTag(t)} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Content>
    </Page>
  );
};
